import { useState } from "react";
import {
  Home, FileText, ClipboardList, Users, Briefcase,
  Receipt, Handshake, GraduationCap, LogIn, Activity,
  Shield, FileSpreadsheet, Calendar, Clock,
  ChevronLeft, ChevronRight, Menu, BookOpen, ClipboardCheck, Award
} from "lucide-react";

const SECTIONS = [
  {
    id: 'main',
    label: null,
    items: [
      { value: 'home', label: 'Home', icon: Home }
    ]
  },
  {
    id: 'operativita',
    label: 'Operativita',
    items: [
      { value: 'reports', label: 'Report', icon: FileText },
      { value: 'summary', label: 'Riepilogo', icon: ClipboardList },
      { value: 'turni', label: 'Turni', icon: Calendar },
      { value: 'planning', label: 'Planning Orario', icon: Clock }
    ]
  },
  {
    id: 'gestione',
    label: 'Gestione',
    items: [
      { value: 'users', label: 'Utenti', icon: Users },
      { value: 'figure', label: 'Figure Prof.', icon: Briefcase },
      { value: 'formazione', label: 'Formazione', icon: GraduationCap },
      { value: 'procedure', label: 'Procedure', icon: BookOpen },
      { value: 'audit', label: 'Audit', icon: ClipboardCheck },
      { value: 'accreditamenti', label: 'Accreditamenti', icon: Award }
    ]
  },
  {
    id: 'amministrazione',
    label: 'Amministrazione',
    items: [
      { value: 'fatture', label: 'Fatture', icon: Receipt },
      { value: 'convenzioni', label: 'Convenzioni', icon: Handshake },
      { value: 'preventivi', label: 'Preventivi', icon: FileSpreadsheet }
    ]
  },
  {
    id: 'sistema',
    label: 'Sistema',
    items: [
      { value: 'login-monitoring', label: 'Login', icon: LogIn },
      { value: 'monitoraggio', label: 'Monitoraggio', icon: Activity },
      { value: 'privacy', label: 'Informative', icon: Shield }
    ]
  }
];

export default function AdminSidebar({ activeTab, onTabChange, userRole, user }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isSuperAdmin = userRole === 'super_amministratore';
  const hasAccreditamentiAccess = isSuperAdmin || user?.has_accreditamenti_access === true;

  const filteredSections = SECTIONS.map(section => ({
    ...section,
    items: section.items.filter(item => {
      if (['figure', 'fatture', 'convenzioni', 'formazione', 'login-monitoring', 'monitoraggio', 'privacy'].includes(item.value)) {
        return isSuperAdmin;
      }
      if (item.value === 'accreditamenti') {
        return hasAccreditamentiAccess;
      }
      return true;
    })
  })).filter(section => section.items.length > 0);

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Collapse toggle - desktop */}
      <div className="hidden lg:flex justify-end p-2 border-b border-gray-100">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="sidebar-collapse-toggle"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-2">
        {filteredSections.map((section) => (
          <div key={section.id} className="mb-1">
            {section.label && !collapsed && (
              <p className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                {section.label}
              </p>
            )}
            {section.label && collapsed && (
              <div className="my-2 mx-2 border-t border-gray-100" />
            )}
            {section.items.map((item) => {
              const isActive = activeTab === item.value;
              const Icon = item.icon;
              return (
                <button
                  key={item.value}
                  onClick={() => {
                    onTabChange(item.value);
                    setMobileOpen(false);
                  }}
                  data-testid={`sidebar-${item.value}`}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all mb-0.5
                    ${isActive
                      ? 'bg-cyan-50 text-cyan-700 shadow-sm border border-cyan-100'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-transparent'
                    }
                    ${collapsed ? 'justify-center px-2' : ''}
                  `}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? 'text-cyan-600' : 'text-gray-400'}`} />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                  {isActive && !collapsed && (
                    <div className="ml-auto h-1.5 w-1.5 rounded-full bg-cyan-500" />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-3 left-3 z-50 p-2 rounded-lg bg-white shadow-md border border-gray-200 text-gray-600 hover:text-gray-900"
        data-testid="sidebar-mobile-toggle"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <div className="relative w-64 bg-white shadow-xl z-50">
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:block shrink-0 bg-white border-r border-gray-200 transition-all duration-200 ${collapsed ? 'w-16' : 'w-56'}`}
        data-testid="admin-sidebar"
      >
        {sidebarContent}
      </aside>
    </>
  );
}
